import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Logo } from '../components/Logo';

export default function PrivacyPolicyPage() {
  const lastUpdated = "October 2026";
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-9 h-9 rounded-md flex items-center justify-center overflow-hidden">
                <Logo className="w-9 h-9" alt="" />
              </div>
              <span className="font-medium text-lg">No Half Days</span>
            </Link>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="page-title mb-2">Privacy policy</h1>
          <p className="text-muted-foreground text-lg mb-8">Last updated: {lastUpdated}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              No Half Days, operated by Your Figment Pty Ltd ("we", "our", or "us"), is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our job management and equipment tracking platform at nohalfdays.com (the "Service").
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              By using our Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-medium mb-3">2.1 Account Information</h3>
            <p className="text-muted-foreground leading-relaxed">When you create an account, we collect:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Full name</li>
              <li>Email address</li>
              <li>Password (stored securely using bcrypt hashing)</li>
              <li>Profile picture (if signing in with Google)</li>
              <li>Authentication method (email/password or Google OAuth)</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 mt-6">2.2 Business Information</h3>
            <p className="text-muted-foreground leading-relaxed">To use our estimating and invoicing features, you may provide:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Business name (company name, trading name, or registered name)</li>
              <li>Business address, phone number, and email</li>
              <li>ABN (Australian Business Number) or equivalent business identifier</li>
              <li>Business logo</li>
              <li>GST/VAT registration status and rate</li>
              <li>Region/country preference</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 mt-6">2.3 Job and Project Data</h3>
            <p className="text-muted-foreground leading-relaxed">When you use our job management features, we store:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Job/project names and descriptions</li>
              <li>Client information (business name, billing address, contact details)</li>
              <li>Shoot dates and schedules</li>
              <li>Phase rates and labour calculations</li>
              <li>Day sheets with call times, wrap times, and break information</li>
              <li>Equipment usage per job</li>
              <li>Expense line items</li>
              <li>Invoice totals and GST calculations</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 mt-6">2.4 Equipment Data</h3>
            <p className="text-muted-foreground leading-relaxed">Our equipment tracking feature stores:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Equipment names, descriptions, and categories</li>
              <li>Purchase prices and dates</li>
              <li>Daily rental rates</li>
              <li>Serial numbers (for your internal tracking only)</li>
              <li>Usage history and income tracking</li>
              <li>ROI calculations</li>
              <li>Disposal/sale information</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 mt-6">2.5 Third-Party Integration Data</h3>
            <p className="text-muted-foreground leading-relaxed">If you connect third-party services:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li><strong>Xero:</strong> OAuth tokens, tenant ID, and account mappings for exporting job data</li>
              <li><strong>Google:</strong> Basic profile information when using Google Sign-In</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 mt-6">2.6 Technical Data</h3>
            <p className="text-muted-foreground leading-relaxed">We automatically collect:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>IP address (for rate limiting and security)</li>
              <li>Browser type and version</li>
              <li>Session tokens and authentication data</li>
              <li>Last login timestamps</li>
              <li>How you use the website and the app: pages visited, buttons clicked, and your device and browser, collected through PostHog and Umami (see section 7)</li>
              <li>How you found us, if you create an account: the ad or site that brought you to the website, and whether you tried the demo first (see section 7)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Provide, maintain, and improve our Service</li>
              <li>Create and manage your account</li>
              <li>Generate job estimates and PDF documents</li>
              <li>Calculate equipment ROI and usage statistics</li>
              <li>Track job profitability and labour costs</li>
              <li>Sync data with connected third-party services (e.g., Xero)</li>
              <li>Send service-related communications</li>
              <li>Provide customer support</li>
              <li>Protect against fraudulent or unauthorized activity</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Data Storage and Security</h2>
            
            <h3 className="text-xl font-medium mb-3">4.1 Where We Store Your Data</h3>
            <p className="text-muted-foreground leading-relaxed">
              The Service runs on Fly.io in their Sydney (syd) region, and your data is stored on MongoDB Atlas in Sydney, Australia. Both the application and the database are located in Australia. We use industry-standard security measures including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Encryption in transit (TLS/SSL)</li>
              <li>Encryption at rest, provided by our database host (MongoDB Atlas)</li>
              <li>Secure password hashing (bcrypt with 12 rounds)</li>
              <li>Rate limiting to prevent abuse</li>
              <li>Secure session management with HTTP-only cookies</li>
            </ul>

            <h3 className="text-xl font-medium mb-3 mt-6">4.2 Data Retention and Deletion</h3>
            <p className="text-muted-foreground leading-relaxed">
              We retain your data for as long as your account is active. If you delete your account, all associated data (jobs, equipment, clients, settings) is <strong>immediately and permanently deleted</strong>. This deletion cannot be undone or recovered. We recommend exporting any data you wish to keep before deleting your account.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              Demo session data is automatically purged after 1 hour.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-muted-foreground leading-relaxed">We do not sell your personal information. We may share your information only in these circumstances:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li><strong>With your consent:</strong> When you explicitly connect third-party services like Xero</li>
              <li><strong>Service providers:</strong> Cloud infrastructure providers (Fly.io for application hosting, MongoDB Atlas for data storage) who help us operate the Service</li>
              <li><strong>Product analytics:</strong> PostHog and Umami, which record how the website and the app are used so we can improve them (see section 7)</li>
              <li><strong>Payment processing:</strong> Polar, our Merchant of Record, receives the billing details needed to take payment and issue invoices</li>
              <li><strong>Legal requirements:</strong> If required by law, regulation, or legal process</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Your Rights and Choices</h2>
            <p className="text-muted-foreground leading-relaxed">You have the right to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li><strong>Access:</strong> View all data associated with your account through the Settings page</li>
              <li><strong>Correction:</strong> Update your account information at any time</li>
              <li><strong>Deletion:</strong> Delete your account and all associated data from Settings &gt; Account</li>
              <li><strong>Data portability:</strong> Export your job and equipment data (PDF exports available)</li>
              <li><strong>Withdraw consent:</strong> Disconnect third-party integrations at any time</li>
              <li><strong>Opt-out:</strong> Disable email notifications in Settings</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Cookies and Tracking</h2>
            <p className="text-muted-foreground leading-relaxed">We set two kinds of cookie:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li><strong>session_token:</strong> HTTP-only, secure cookie for maintaining your login session. The Service cannot work without it.</li>
              <li><strong>Analytics (ph_…_posthog):</strong> a cookie and a browser storage entry set by PostHog, holding a random identifier so visits from the same browser can be counted together.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We use PostHog to understand how people use the website and the app: which pages are visited, what is clicked, and where people stop, for example when trying the demo. This can include recordings of how a page was used, with anything you type hidden. We don&apos;t send PostHog your name or email address with these events. PostHog processes this data in the United States.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We also use Umami to count visits: which pages are viewed, the site you came from, and your browser, device and country. Umami sets no cookies and doesn&apos;t identify you.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              When you first visit, your browser keeps a note of how you arrived (<strong>nhd_first_visit</strong>, a browser storage entry): the campaign name in the link you followed, the site you came from, and whether you opened the link inside Instagram or Facebook. If you create an account, it is saved with your account so we can see which of our ads and links bring people who sign up. It stays with us and is never sent to an analytics or advertising service.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We don&apos;t use advertising cookies, and we don&apos;t sell analytics data or share it with advertisers. You can block analytics with your browser&apos;s tracking protection or a content blocker; the Service works the same without it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed">Our Service integrates with:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li><strong>Google Sign-In:</strong> For optional social authentication. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google's Privacy Policy</a></li>
              <li><strong>Sign in with Apple and Microsoft:</strong> Also optional. See <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Apple's Privacy Policy</a> and <a href="https://privacy.microsoft.com/privacystatement" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft's Privacy Statement</a></li>
              <li><strong>Xero:</strong> For accounting integration. See <a href="https://www.xero.com/au/about/legal/privacy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Xero's Privacy Policy</a></li>
              <li><strong>MongoDB Atlas:</strong> For secure data storage. See <a href="https://www.mongodb.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">MongoDB's Privacy Policy</a></li>
              <li><strong>Fly.io:</strong> For application hosting in Sydney, Australia. See <a href="https://fly.io/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Fly.io's Privacy Policy</a></li>
              <li><strong>PostHog:</strong> For product analytics (see section 7). See <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">PostHog's Privacy Policy</a></li>
              <li><strong>Umami:</strong> For counting visits (see section 7). See <a href="https://umami.is/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Umami&apos;s Privacy Policy</a></li>
              <li><strong>Polar:</strong> Our Merchant of Record, for subscription payments and invoicing. See <a href="https://polar.sh/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Polar's Privacy Policy</a></li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. International Users</h2>
            <p className="text-muted-foreground leading-relaxed">
              While No Half Days is primarily designed for Australian freelancers and video professionals, we support users from the United States, United Kingdom, and European Union. Our cloud infrastructure is located in Sydney, Australia, so if you are outside Australia your data is transferred to and processed there.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              For EU users: We process your data based on legitimate interests (providing the Service you requested) and, where applicable, your explicit consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. For significant changes, we may also notify you via email or through an in-app announcement.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-foreground font-medium">Your Figment Pty Ltd</p>
              <p className="text-muted-foreground">Email: crew@nohalfdays.com</p>
              <p className="text-muted-foreground">Website: nohalfdays.com</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Your Figment Pty Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
              Terms of Service
            </Link>
            <Link to="/refund-policy" className="text-muted-foreground hover:text-foreground transition-colors">
              Refund Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
